<template>
  <div class="register-page">
    <img class="register-page__background" :src="registerBackground" alt="" aria-hidden="true" />
    <div class="register-page__overlay" aria-hidden="true"></div>
    <div class="register-page__glow" aria-hidden="true"></div>

    <main class="register-layout">
      <section class="register-panel" aria-labelledby="register-title">
        <header class="register-panel__header">
          <img class="register-logo" :src="brandLogo" alt="Fusco Fit" />
          <p class="register-kicker">
            <span aria-hidden="true"></span>
            Nuovo profilo
          </p>
          <h1 id="register-title">Crea il tuo account</h1>
          <p class="register-description">
            Usa un indirizzo email funzionante: sarà il riferimento per accedere e recuperare la password.
          </p>
        </header>

        <form class="register-form" @submit.prevent="handleRegister">
          <div class="register-name-grid">
            <label class="register-field">
              <span>Nome</span>
              <span class="register-field__control">
                <span class="material-icons" aria-hidden="true">person_outline</span>
                <input v-model="name" type="text" autocomplete="given-name" placeholder="Nome" required />
              </span>
            </label>

            <label class="register-field">
              <span>Cognome</span>
              <span class="register-field__control">
                <span class="material-icons" aria-hidden="true">badge</span>
                <input v-model="surname" type="text" autocomplete="family-name" placeholder="Cognome" required />
              </span>
            </label>
          </div>

          <label class="register-field">
            <span>Email</span>
            <span class="register-field__control">
              <span class="material-icons" aria-hidden="true">mail_outline</span>
              <input
                v-model="email"
                type="email"
                inputmode="email"
                autocomplete="email"
                placeholder="nome@email.it"
                required
              />
            </span>
          </label>

          <label class="register-field">
            <span>Numero di telefono</span>
            <span class="register-field__control">
              <span class="material-icons" aria-hidden="true">phone_iphone</span>
              <input
                v-model="phone"
                type="tel"
                inputmode="tel"
                autocomplete="tel"
                placeholder="+39 327 123 4567"
                pattern="[+0-9 ()-]{8,24}"
                required
              />
            </span>
          </label>

          <label class="register-field">
            <span class="register-field__label">
              <span>Password</span>
              <small>Minimo 6 caratteri</small>
            </span>
            <span class="register-field__control">
              <span class="material-icons" aria-hidden="true">lock_outline</span>
              <input
                v-model="password"
                type="password"
                autocomplete="new-password"
                minlength="6"
                placeholder="Crea una password"
                required
              />
            </span>
          </label>

          <label class="register-field">
            <span>Conferma password</span>
            <span class="register-field__control">
              <span class="material-icons" aria-hidden="true">verified_user</span>
              <input
                v-model="confirmPassword"
                type="password"
                autocomplete="new-password"
                minlength="6"
                placeholder="Ripeti la password"
                required
              />
            </span>
          </label>

          <p v-if="errorMessage" class="register-message" role="alert" aria-live="polite">
            <span class="material-icons" aria-hidden="true">error_outline</span>
            <span>{{ errorMessage }}</span>
          </p>

          <button class="register-submit" type="submit" :disabled="isRegistering">
            <span>{{ registerButtonLabel }}</span>
            <span class="material-icons" aria-hidden="true">arrow_forward</span>
          </button>

          <router-link class="register-login-link" to="/">
            <span class="material-icons" aria-hidden="true">arrow_back</span>
            <span>Hai già un account? Accedi</span>
          </router-link>
        </form>

        <footer class="register-panel__footer">
          <span class="material-icons" aria-hidden="true">shield</span>
          <span>I tuoi dati sono protetti</span>
        </footer>
      </section>
    </main>
  </div>
</template>


<script>

import { computed, ref } from 'vue';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useRouter } from 'vue-router';
import { auth, db } from '@/firebaseConfig';
import registerBackground from '../../fuscofit/assets/hero_coachingday.png';
import brandLogo from '../../fuscofit/assets/logo-white.png';
import { isValidUserPhone, normalizeUserPhone } from '@/lib/userPhone';

const withTimeout = (promise, timeoutMs, message, code) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => {
      const error = new Error(message);
      error.code = code;
      reject(error);
    }, timeoutMs);
  });

  return Promise.race([
    Promise.resolve(promise).finally(() => window.clearTimeout(timeoutId)),
    timeoutPromise,
  ]);
};

const getRegistrationErrorMessage = (error) => {
  if (error?.code === 'auth/email-already-in-use') {
    return 'Questa email è già registrata. Torna alla schermata di accesso.';
  }
  if (error?.code === 'auth/weak-password') {
    return 'La password deve contenere almeno 6 caratteri.';
  }
  if (error?.code === 'auth/invalid-email') {
    return 'Inserisci un indirizzo email valido.';
  }
  if (error?.code === 'app/register-timeout') {
    return 'Il servizio di registrazione non ha risposto. Riprova tra qualche secondo.';
  }
  if (error?.code === 'auth/network-request-failed') {
    return 'Firebase non è raggiungibile in questo momento. Riprova tra qualche secondo.';
  }
  return 'Registrazione non completata. Riprova tra qualche secondo.';
};

export default {
  setup() {
    const email = ref("");
    const phone = ref("");
    const password = ref("");
    const confirmPassword = ref("");
    const name = ref("");  // Cambiato da firstName a name
    const surname = ref("");  // Cambiato da lastName a surname
    const errorMessage = ref(""); // Nuova variabile ref per gestire i messaggi di errore
    const isRegistering = ref(false);
    const registrationStage = ref('idle');
    // ... altri campi

    const router = useRouter();
    const normalizeEmail = (value) => (value || '').toString().trim().toLowerCase();
    const registerButtonLabel = computed(() => {
      if (registrationStage.value === 'profile') return 'Completamento profilo…';
      if (isRegistering.value) return 'Creazione account…';
      return 'Crea account';
    });

    const handleRegister = async () => {
      if (isRegistering.value) return;

      errorMessage.value = "";

      const normalizedEmail = normalizeEmail(email.value);
      const normalizedPhone = normalizeUserPhone(phone.value);

      if (!isValidUserPhone(normalizedPhone)) {
        errorMessage.value = "Inserisci un numero di telefono valido, completo di prefisso internazionale.";
        return;
      }

      if(password.value !== confirmPassword.value) {
        errorMessage.value = "Le password non corrispondono.";
        return;
      }

      isRegistering.value = true;
      registrationStage.value = 'auth';

      try {
        // Firebase Auth e l'unica fonte affidabile per sapere se l'email e gia
        // registrata. La vecchia lettura Firestore usava l'email come id mentre
        // i profili sono salvati con l'UID, aggiungendo solo latenza.
        const userCredential = await withTimeout(
          createUserWithEmailAndPassword(auth, normalizedEmail, password.value),
          10000,
          'Timeout durante la creazione dell’account.',
          'app/register-timeout'
        );

        const userId = userCredential.user.uid;
        const profile = {
          uid: userId,
          name: name.value.trim(),
          surname: surname.value.trim(),
          email: normalizedEmail,
          phone: normalizedPhone,
        };

        registrationStage.value = 'profile';
        localStorage.setItem('pendingUserProfile', JSON.stringify(profile));

        const profileWrite = setDoc(doc(db, 'users', userId), {
          ...profile,
          created_time: serverTimestamp(),
        }, { merge: true });

        try {
          await withTimeout(
            profileWrite,
            4000,
            'Timeout durante il salvataggio del profilo.',
            'app/profile-timeout'
          );
          localStorage.removeItem('pendingUserProfile');
        } catch (profileError) {
          // L'account Auth esiste gia: non costringiamo l'utente a registrarsi
          // di nuovo. Il bootstrap ritentera automaticamente questo salvataggio.
          console.warn('Profilo in attesa di sincronizzazione:', profileError);
          profileWrite
            .then(() => localStorage.removeItem('pendingUserProfile'))
            .catch(() => {});
        }

        localStorage.setItem('authUid', userId);
        localStorage.setItem('userRole', 'User');
        await router.replace('/user');
      } catch (error) {
        console.error('Errore durante la registrazione:', error);
        errorMessage.value = getRegistrationErrorMessage(error);
      } finally {
        isRegistering.value = false;
        registrationStage.value = 'idle';
      }
    };

    return {
      brandLogo,
      email,
      phone,
      password,
      name,  // Cambiato da firstName a name
      registerBackground,
      surname,  // Cambiato da lastName a surname
      confirmPassword,
      handleRegister,
      errorMessage,
      isRegistering,
      registerButtonLabel,
      // ... altri campi e metodi
    };
  },
};

</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Montserrat:wght@700;800;900&display=swap');

:global(body) {
  margin: 0;
  background: #07090a;
}

:global(#app) {
  min-height: 100vh;
}

.register-page,
.register-page *,
.register-page *::before,
.register-page *::after {
  box-sizing: border-box;
}

.register-page {
  --register-red: #d50b35;
  --register-red-dark: #a90f29;
  --register-white: #ffffff;
  position: relative;
  display: grid;
  min-height: 100vh;
  min-height: 100svh;
  overflow: hidden auto;
  background: #07090a;
  color: var(--register-white);
  font-family: 'DM Sans', sans-serif;
  isolation: isolate;
}

.register-page__background,
.register-page__overlay,
.register-page__glow {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
}

.register-page__background {
  z-index: -3;
  object-fit: cover;
  object-position: center;
  filter: saturate(0.78) contrast(1.04);
  transform: scale(1.015);
}

.register-page__overlay {
  z-index: -2;
  background:
    linear-gradient(90deg, rgba(4, 6, 7, 0.96) 0%, rgba(4, 6, 7, 0.82) 34%, rgba(4, 6, 7, 0.34) 70%, rgba(4, 6, 7, 0.26) 100%),
    linear-gradient(0deg, rgba(4, 5, 6, 0.66) 0%, transparent 48%, rgba(4, 5, 6, 0.14) 100%);
}

.register-page__glow {
  z-index: -1;
  background:
    radial-gradient(circle at 12% 88%, rgba(213, 11, 53, 0.2), transparent 28rem),
    radial-gradient(circle at 88% 7%, rgba(11, 143, 170, 0.12), transparent 28rem);
  pointer-events: none;
}

.register-layout {
  display: flex;
  align-items: center;
  width: min(calc(100% - 64px), 1392px);
  min-height: inherit;
  margin: 0 auto;
  padding: 30px 0;
}

.register-panel {
  width: min(100%, 520px);
  padding: clamp(27px, 3.5vw, 38px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 30px;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.115), rgba(255, 255, 255, 0.045)),
    rgba(6, 8, 9, 0.5);
  box-shadow:
    0 34px 90px rgba(0, 0, 0, 0.46),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(30px) saturate(1.25);
  -webkit-backdrop-filter: blur(30px) saturate(1.25);
}

.register-panel__header {
  text-align: left;
}

.register-logo {
  display: block;
  width: 90px;
  height: auto;
  margin: 0 0 22px;
  object-fit: contain;
}

.register-kicker {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 10px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.register-kicker > span {
  width: 27px;
  height: 3px;
  border-radius: 999px;
  background: var(--register-red);
}

.register-panel h1 {
  max-width: 440px;
  margin: 0;
  color: var(--register-white);
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(2rem, 4vw, 2.85rem);
  font-weight: 900;
  line-height: 0.94;
  letter-spacing: -0.06em;
  text-transform: uppercase;
}

.register-description {
  max-width: 420px;
  margin: 15px 0 0;
  color: rgba(255, 255, 255, 0.68);
  font-size: var(--ff-type-body);
  line-height: 1.5;
}

.register-form {
  display: grid;
  gap: 12px;
  width: 100%;
  margin-top: 23px;
}

.register-name-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.register-field {
  display: grid;
  gap: 6px;
  min-width: 0;
  color: rgba(255, 255, 255, 0.76);
  font-size: var(--ff-type-label);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.register-field__label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.register-field__label small {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: none;
}

.register-field__control {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  align-items: center;
  gap: 9px;
  min-height: 50px;
  padding: 0 13px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.09);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.register-field__control:focus-within {
  border-color: rgba(255, 255, 255, 0.54);
  background: rgba(255, 255, 255, 0.13);
  box-shadow:
    0 0 0 3px rgba(213, 11, 53, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.register-field__control .material-icons {
  color: rgba(255, 255, 255, 0.66);
  font-size: 19px;
}

.register-field input {
  width: 100%;
  min-width: 0;
  padding: 14px 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--register-white);
  font: 500 0.9rem/1.2 'DM Sans', sans-serif;
  letter-spacing: normal;
  text-transform: none;
}

.register-field input::placeholder {
  color: rgba(255, 255, 255, 0.36);
  opacity: 1;
}

.register-submit {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 52px;
  margin-top: 2px;
  padding: 0 16px 0 19px;
  border: 0;
  border-radius: 15px;
  background: linear-gradient(135deg, #e20b39, var(--register-red-dark));
  color: var(--register-white);
  font-family: 'DM Sans', sans-serif;
  font-size: 0.9375rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  box-shadow: 0 15px 28px rgba(213, 11, 53, 0.28);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.register-submit:hover {
  filter: brightness(1.08);
  box-shadow: 0 18px 34px rgba(213, 11, 53, 0.36);
  transform: translateY(-2px);
}

.register-submit:disabled {
  cursor: wait;
  opacity: 0.68;
  filter: none;
  transform: none;
}

.register-submit .material-icons {
  color: currentColor;
  font-size: 20px;
}

.register-message {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  align-items: start;
  gap: 8px;
  margin: 0;
  padding: 11px 12px;
  border: 1px solid rgba(255, 83, 113, 0.3);
  border-radius: 12px;
  background: rgba(213, 11, 53, 0.14);
  color: #ffd7df;
  font-size: var(--ff-type-body-small);
  line-height: 1.4;
}

.register-message .material-icons {
  font-size: 18px;
}

.register-login-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  justify-self: center;
  padding: 4px 5px;
  color: rgba(255, 255, 255, 0.74);
  font-size: var(--ff-type-body-small);
  font-weight: 600;
  text-decoration: none;
}

.register-login-link:hover {
  color: var(--register-white);
}

.register-login-link .material-icons {
  color: currentColor;
  font-size: 17px;
}

.register-submit:focus-visible,
.register-login-link:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.52);
  outline-offset: 3px;
}

.register-panel__footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 19px;
  padding-top: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.11);
  color: rgba(255, 255, 255, 0.42);
  font-size: var(--ff-type-label);
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.register-panel__footer .material-icons {
  color: rgba(255, 255, 255, 0.52);
  font-size: 16px;
}

@media (max-width: 640px) {
  .register-page__background {
    object-position: 76% center;
  }

  .register-page__overlay {
    background:
      linear-gradient(0deg, rgba(4, 6, 7, 0.82) 0%, rgba(4, 6, 7, 0.58) 58%, rgba(4, 6, 7, 0.38) 100%),
      linear-gradient(90deg, rgba(4, 6, 7, 0.44), rgba(4, 6, 7, 0.18));
  }

  .register-layout {
    align-items: flex-start;
    width: calc(100% - 24px);
    padding:
      calc(12px + env(safe-area-inset-top, 0px))
      0
      calc(26px + env(safe-area-inset-bottom, 0px));
  }

  .register-panel {
    padding: 21px 18px 18px;
    border-radius: 24px;
    background:
      linear-gradient(145deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.045)),
      rgba(6, 8, 9, 0.47);
  }

  .register-logo {
    width: 72px;
    margin-bottom: 16px;
  }

  .register-panel h1 {
    font-size: clamp(1.8rem, 8.6vw, 2.2rem);
  }

  .register-description {
    margin-top: 11px;
    font-size: var(--ff-type-body-small);
    line-height: 1.42;
  }

  .register-form {
    gap: 10px;
    margin-top: 18px;
  }

  .register-field__control {
    min-height: 47px;
    padding: 0 11px;
    border-radius: 14px;
  }

  .register-field input {
    padding: 12px 0;
    font-size: 0.84rem;
  }

  .register-submit {
    min-height: 49px;
    border-radius: 14px;
  }

  .register-panel__footer {
    margin-top: 14px;
    padding-top: 13px;
  }
}

@media (max-width: 370px) {
  .register-name-grid {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .register-field__control,
  .register-submit {
    transition: none;
  }
}
</style>
